import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { components as RSComponents } from "react-select";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { useNavigate } from "react-router-dom";
import styles from "./SilcoBagWeight.module.css";
import FloatingActions from "../../components/FloatingActions";

const SilcoCaptiveBagWeight = () => {
  const user = useSelector(selectUser);
  const token = user?.token;
  const emp_id = user?.emp_id;

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [popup, setPopup] = useState({ open: false, title: "", message: "" });

  // LOVs
  const [truckOptions, setTruckOptions] = useState([]);
  const [bagOptions, setBagOptions] = useState([]);

  // Selected values
  const [selectedTruckOpt, setSelectedTruckOpt] = useState(null); // react-select option
  const [selectedTruckData, setSelectedTruckData] = useState(null); // raw row
  const [selectedBagOpt, setSelectedBagOpt] = useState(null);

  // Inputs
  const [noOfBags, setNoOfBags] = useState("");

  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const navigate = useNavigate();

  const showPopup = (message, title = "Notice") =>
    setPopup({ open: true, title, message });
  const closePopup = () => setPopup({ open: false, title: "", message: "" });

  // Derived: per-bag (from Bag Code) and pack weight
  const perBagFromCode = useMemo(() => {
    const v = selectedBagOpt?.perBundle;
    return v === undefined || v === null || v === "" ? "" : String(v);
  }, [selectedBagOpt]);

  const packWt = useMemo(() => {
    const n = parseFloat(noOfBags || "0");
    const p = parseFloat(perBagFromCode || "0");
    if (Number.isNaN(n) || Number.isNaN(p)) return "0.000";
    return (n * p * 0.001).toFixed(2);
  }, [noOfBags, perBagFromCode]);

  // Load LOVs
  const fetchTrucks = async () => {
    try {
      const res = await axios.post("/api/wbauto/getSilcoCaptiveTrucksForBagWt", {
        emp_id,
        token,
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setTruckOptions(
        data.map((r) => ({
          value: r.P_DOCNUMBER,
          label: `${r.P_TRUCK} — ${r.P_ITEM_NAME}`,
          data: r,
        }))
      );
    } catch (err) {
      console.error("getSilcoTrucksForBagWt error:", err);
      showPopup(
        err?.response?.data?.message || "Failed to load trucks.",
        "Error"
      );
      setTruckOptions([]);
    }
  };

  const fetchBagCodes = async () => {
    try {
      const res = await axios.post("/api/wbauto/getBagCodes", {
        emp_id,
        token,
      });
      const data = Array.isArray(res.data) ? res.data : [];
      // Expecting columns: BagCode, BagWeight, Bagcapacity, Bagtype
      setBagOptions(
        data.map((r) => ({
          value: r.Code,
          // keep label minimal; we'll render columns via formatOptionLabel
          label: r.Code,
          bagWeight: r.BagWeight,       // used for column + per-bag weight
          bagCapacity: r.Bagcapacity,   // as provided by API
          bagType: r.Bagtype,           // as provided by API
          perBundle: r.BagWeight ?? null, // <- this drives Per-Bag Weight field
          raw: r,
        }))
      );
      
    } catch (err) {
      console.error("getBagCodes error:", err);
      showPopup(
        err?.response?.data?.message || "Failed to load bag codes.",
        "Error"
      );
      setBagOptions([]);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchTrucks();
    fetchBagCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // When truck changes, set raw data and reset inputs
  const onTruckChange = (opt) => {
    setSelectedTruckOpt(opt || null);
    setSelectedTruckData(opt?.data || null);
    setNoOfBags("");
    setSelectedBagOpt(null);
  };

  const validate = () => {
    if (!selectedTruckData) return "Please select a Truck.";
    if (!selectedBagOpt) return "Please select a Bag Code.";
    if (perBagFromCode === "" || isNaN(Number(perBagFromCode)) || Number(perBagFromCode) <= 0)
      return "Selected Bag Code has no valid per-bag weight in master.";
    if (noOfBags === "" || isNaN(Number(noOfBags)) || Number(noOfBags) <= 0)
      return "Enter a valid No. of Bags.";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setToast(`❌ ${err}`);
      topRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const ok = window.confirm(
      `Update bag weight for Truck ${selectedTruckData.P_TRUCK}?`
    );
    if (!ok) return;

    try {
      setLoading(true);

      const payload = {
        emp_id,
        token,
        data: {
          DocNumber: selectedTruckData.P_DOCNUMBER,
          Truck: selectedTruckData.P_TRUCK,
          BagWtCode: selectedBagOpt.value,
          NoOfBundle: Number(noOfBags),
          PerBundleWt: Number(perBagFromCode), // from Bag Code only (read-only)
          PackWt: Number((Number(noOfBags) * Number(perBagFromCode) * 0.001).toFixed(2)),
        },
      };

      const res = await axios.post(
        "/api/wbauto/updateSilcoCaptiveTrucksForBagWt",
        payload
      );
      if (res.data?.success) {
        setToast("✅ Bag weight updated.");
        // keep selection; just clear count
        setNoOfBags("");
      } else {
        const msg = res.data?.message || "Failed to update.";
        setToast(`❌ ${msg}`);
        showPopup(msg, "Error");
      }
    } catch (error) {
      console.error("updateSilcoTrucksForBagWt error:", error);
      const msg = error?.response?.data?.message || "Error updating record.";
      setToast(`❌ ${msg}`);
      showPopup(msg, "Error");
    } finally {
      setLoading(false);
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Auto-hide toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth" });
  const scrollBottom = () => bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  // Header row for the dropdown menu
const BagMenuList = (props) => (
  <RSComponents.MenuList {...props}>
    <div className={styles.bagMenuHeader}>
      <span className={styles.colCode}>Code</span>
      <span className={styles.colWt}>BagWeight</span>
      <span className={styles.colCap}>Bagcapacity</span>
      <span className={styles.colType}>Bagtype</span>
    </div>
    {props.children}
  </RSComponents.MenuList>
);

  // Render each option row as 4 "columns"
  const formatBagOptionLabel = (opt, { context }) => {
    if (context === "menu") {
      return (
        <div className={styles.bagOptionRow} title={opt.value}>
          <span className={styles.colCode}>{opt.value ?? ""}</span>
          <span className={styles.colWt}>{opt.bagWeight ?? ""}</span>
          <span className={styles.colCap}>{opt.bagCapacity ?? ""}</span>
          <span className={styles.colType}>{opt.bagType ?? ""}</span>
        </div>
      );
    }
    // How it looks once selected (condensed)
    return `${opt.value ?? ""} — ${opt.bagWeight ?? ""} / ${opt.bagCapacity ?? ""} / ${opt.bagType ?? ""}`;
  };


  if (!token) {
    return (
      <div className={styles.approveContainer}>
        <p>You must be logged in to update bag weight.</p>
        <button className={styles.cancelBtn} onClick={() => navigate("/")}>
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className={styles.approveContainer}>
      <div ref={topRef} />
      <h2 className={styles.screenTitle}>Silco Captive Bag Weight Update</h2>

      {toast && <div className={styles.toast}>{toast}</div>}

      {popup.open && (
        <div className={styles.modalBackdrop} onClick={closePopup}>
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            <h4 className={styles.modalTitle}>{popup.title}</h4>
            <p className={styles.modalBody}>{popup.message}</p>
            <button className={styles.modalCloseBtn} onClick={closePopup}>OK</button>
          </div>
        </div>
      )}

      {/* Pickers */}
      <div className={styles.filters}>
        <label>Truck (searchable):</label>
        <Select
          options={truckOptions}
          value={selectedTruckOpt}
          onChange={onTruckChange}
          className={styles.select}
          classNamePrefix="rs"
          isClearable
          placeholder="Select Truck…"
        />

        {/* Card */}
        {selectedTruckData && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.avatar}>
                {String(selectedTruckData.P_TRUCK || "?")
                  .charAt(0)
                  .toUpperCase()}
              </div>
              <div className={styles.headerText}>
                <div className={styles.title}>{selectedTruckData.P_TRUCK}</div>
                <div className={styles.subTitle}>
                  Doc: {selectedTruckData.P_DOCNUMBER}
                </div>
              </div>
            </div>

            <div className={styles.cardBody}>
              <div className={styles.row}>
                <span>Item Catg</span>
                <strong>{selectedTruckData.P_ITEM_CATG}</strong>
              </div>
              <div className={styles.row}>
                <span>Item Code</span>
                <strong>{selectedTruckData.P_ITEM_CD}</strong>
              </div>
              <div className={styles.row}>
                <span>Item Name</span>
                <strong>{selectedTruckData.P_ITEM_NAME}</strong>
              </div>
              <div className={styles.row}>
                <span>Existing:</span>
              </div>
              <div className={styles.row}>
                <span>BagCode</span>
                <strong>{selectedTruckData.P_BAGWTCODE}</strong>
              </div>
              <div className={styles.row}>
                <span>No. of Bags</span>
                <strong>{selectedTruckData.P_NOOFBUNDLE}</strong>
              </div>
              <div className={styles.row}>
                <span>Per-Bag Weight</span>
                <strong>{selectedTruckData.P_PERBUNDLEWT}</strong>
              </div>
              <div className={styles.row}>
                <span>Pack Weight</span>
                <strong>{selectedTruckData.P_PACKWT}</strong>
              </div>

            </div>
          </div>
        )}

        <label>Bag Code:</label>
        <Select
          options={bagOptions}
          value={selectedBagOpt}
          onChange={setSelectedBagOpt}
          className={styles.select}
          classNamePrefix="rs"
          isClearable
          placeholder="Select Bag Code…"
          isDisabled={!selectedTruckOpt}
          // multi-column UI:
          components={{ MenuList: BagMenuList }}
          formatOptionLabel={formatBagOptionLabel}
        />
      </div>

      {/* Inputs */}
      <div className={styles.formGrid}>
        <div className={styles.formItem}>
          <label>No. of Bags</label>
          <input
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            className={styles.input}
            value={noOfBags}
            onChange={(e) => setNoOfBags(e.target.value)}
            disabled={!selectedTruckData}
            placeholder="Enter count"
          />
        </div>

        <div className={styles.formItem}>
          <label>Per-Bag Weight</label>
          <input
            type="text"
            className={`${styles.input} ${styles.readonly}`}
            value={perBagFromCode ? `${perBagFromCode}` : ""}
            readOnly
            placeholder="Auto from Bag Code"
          />
        </div>

        <div className={styles.formItem}>
          <label>Pack Weight (preview)</label>
          <input
            type="text"
            className={styles.input}
            value={`${packWt} (MT)`}
            readOnly
          />
        </div>
      </div>

      {/* Submit */}
      <button
        className={styles.approveBtn}
        onClick={handleSubmit}
        disabled={
          loading ||
          !selectedTruckData ||
          !selectedBagOpt ||
          !noOfBags ||
          !perBagFromCode
        }
      >
        {loading ? "Saving..." : "Update Bag Weight"}
      </button>

      <button
        className={styles.cancelBtn}
        onClick={() => navigate("/dashboard")}
      >
        Dashboard
      </button>

      {/* Floating: Top • Bottom • Dashboard */}

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

export default SilcoCaptiveBagWeight;
